/**
 * ExamHQ · «штаб подготовки» — кокпит-первый вход в Sprint 06
 * (slot_s06_rework · W5 · 11 JUN 2026 · мокап 04_DESIGN/mockup_s06_hq.html, ack Genie).
 *
 * Заменяет CockpitOverview в Sprint.tsx для examSprint. Два состояния:
 *   1. Даты нет — онбординг: «когда экзамен?» (чипы темпа + своя дата) либо
 *      «просто открой материалы».
 *   2. Дата есть — штаб: сводка (дни · дата · вероятность) + 3 полосы +
 *      план по дням (examPlan.ts · derived, пересобирается сам) + слабые главы
 *      с дриллами (DrillSession) + материалы (6 тикетов) + быстрые действия.
 * Реюз метрик кокпита: computePassProbability / daysToExam (pass-probability.ts).
 * Отличие от мокапа (фикс на ревью): прошедшие дни плана не реконструируются —
 * план показывает только сегодня → экзамен.
 */
import { useMemo, useState } from 'react';
import { useProgress } from '@/contexts/ProgressContext';
import { BLOCK_1_TERMS } from '@shared/data/glossary';
import { ticketsBySprint } from '@shared/data/tickets';
import { computePassProbability, daysToExam } from '@/lib/pass-probability';
import { buildExamPlan, collapsePlan, type PlanTask } from '@/lib/examPlan';
import { DrillSession, CTFL_CHAPTER_RU } from '@/components/cockpit/DrillSession';
import type { ByChapterScore } from '@/types';

interface ExamHQProps {
  onStartMock?: (slot: 'mock1' | 'mock2') => void;
  onOpenFlashcards?: () => void;
  onTicket: (id: string) => void;
}

const fmtDate = (iso: string): string => {
  const d = new Date(iso + 'T12:00:00');
  const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
};

const isoPlusDays = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

/** Свежайший разрез по главам: mock2 > mock1 > Перевал. */
function latestByChapter(progress: ReturnType<typeof useProgress>['progress']): ByChapterScore | null {
  const { mock2, mock1 } = progress.cockpit.mockHistory;
  return mock2?.byChapter ?? mock1?.byChapter ?? progress.cockpit.blockExamResult?.byChapter ?? null;
}

export function ExamHQ({ onStartMock, onOpenFlashcards, onTicket }: ExamHQProps) {
  const { progress, setExamDate } = useProgress();
  const examDate = progress.cockpit.examDate;
  const [pickingDate, setPickingDate] = useState(false);
  const [drillChapter, setDrillChapter] = useState<string | null>(null);

  const flashcards = progress.cockpit.flashcards;
  const learned = useMemo(
    () => Object.values(flashcards).filter((f) => f.reps >= 2).length,
    [flashcards],
  );
  // Знаменатель = Block 1 (S01-S05), набор CTFL-тренажёра (Block 2 на S06 не пройден).
  const glossPct = BLOCK_1_TERMS.length === 0 ? 0 : Math.round((learned / BLOCK_1_TERMS.length) * 100);
  const days = daysToExam(examDate);
  const probability = computePassProbability(progress.cockpit.mockHistory, glossPct, days);

  const tickets = useMemo(() => ticketsBySprint('S06'), []);
  const ticketsDone = tickets.filter((t) => progress.tickets[t.id]?.status === 'done').length;

  const mock1 = progress.cockpit.mockHistory.mock1;
  const mock2 = progress.cockpit.mockHistory.mock2;

  const byChapter = latestByChapter(progress);
  const weakChapters = useMemo(() => {
    if (!byChapter) return [];
    return Object.entries(byChapter)
      .map(([ch, v]) => ({ ch, pct: v.pct }))
      .sort((a, b) => a.pct - b.pct);
  }, [byChapter]);

  const plan = useMemo(
    () => (examDate ? collapsePlan(buildExamPlan(examDate, progress)) : []),
    [examDate, progress],
  );

  /* ── состояние 1 · даты нет ── */
  if (!examDate) {
    return (
      <section className="hq" aria-label="Штаб подготовки к ISTQB">
        <div className="hq-mono-tag">Sprint 06 · CTFL Summit · по желанию</div>
        <h2 className="hq-title">Штаб подготовки к ISTQB</h2>
        <p className="hq-sub">
          Этот спринт устроен не как остальные: вместо ленты тикетов — штаб. Назови дату
          экзамена, и штаб разложит подготовку по дням: глоссарий-тренажёр, два
          mock-экзамена, разборы и повторения. Темп твой: 1-2 недели в параллель с
          основным курсом.
        </p>
        <div className="hq-mono-tag soft">Когда планируешь сдавать?</div>
        <div className="hq-date-chips">
          <button type="button" className="hq-dchip" onClick={() => setExamDate(isoPlusDays(7))}>
            через неделю<small>интенсив · ~45 мин/день</small>
          </button>
          <button type="button" className="hq-dchip" onClick={() => setExamDate(isoPlusDays(14))}>
            через 2 недели<small>ровный темп · ~30 мин/день</small>
          </button>
          <button type="button" className="hq-dchip" onClick={() => setExamDate(isoPlusDays(30))}>
            через месяц<small>спокойно · ~20 мин/день</small>
          </button>
          {pickingDate ? (
            <input
              type="date"
              className="hq-date-input"
              aria-label="Дата экзамена"
              min={isoPlusDays(1)}
              onChange={(e) => e.target.value && setExamDate(e.target.value)}
            />
          ) : (
            <button type="button" className="hq-dchip" onClick={() => setPickingDate(true)}>
              своя дата<small>календарь</small>
            </button>
          )}
        </div>
        <p className="hq-skip">
          пока без даты? материалы спринта открыты ниже — план можно построить в любой
          момент.{' '}
          {progress.cockpit.blockExamResult
            ? 'Результат «Перевала» уже здесь: слабые главы подсветятся сразу.'
            : 'Сдан «Перевал» — штаб сразу подскажет слабые главы.'}
        </p>
      </section>
    );
  }

  /* ── состояние 2 · штаб ── */
  return (
    <section className="hq" aria-label="Штаб подготовки к ISTQB">
      <div className="hq-mono-tag">Sprint 06 · CTFL Summit · штаб подготовки</div>

      <div className="hq-top">
        <div className="hq-days">
          <div className="n">{days ?? 0}</div>
          <div className="lab">{days === 1 ? 'день до экзамена' : 'дней до экзамена'}</div>
        </div>
        <div className="hq-mid">
          <div className="hq-date-line">
            экзамен <b>{fmtDate(examDate)}</b>
          </div>
          <button type="button" className="hq-edit" onClick={() => setExamDate(undefined)}>
            изменить дату
          </button>
        </div>
        <div className="hq-prob">
          <div className="p">{probability}%</div>
          <div className="lab">вероятность сдачи</div>
        </div>
      </div>

      <div className="hq-bars">
        <div className="hq-bar-cell">
          <div className="t">глоссарий освоен</div>
          <div className="v">
            {learned} / {BLOCK_1_TERMS.length}
          </div>
          <div className="hq-bar">
            <i className="teal" style={{ width: `${glossPct}%` }} />
          </div>
        </div>
        <div className="hq-bar-cell">
          <div className="t">{mock2 ? 'Mock #2 · лучший' : 'Mock #1 · лучший'}</div>
          <div className="v">{mock2 ? `${mock2.correct} / 40` : mock1 ? `${mock1.correct} / 40` : '— ещё не сдан'}</div>
          <div className="hq-bar">
            <i className="mustard" style={{ width: `${(mock2 ?? mock1)?.pct ?? 0}%` }} />
          </div>
        </div>
        <div className="hq-bar-cell">
          <div className="t">материалы спринта</div>
          <div className="v">
            {ticketsDone} / {tickets.length}
          </div>
          <div className="hq-bar">
            <i
              className="green"
              style={{ width: `${tickets.length ? Math.round((ticketsDone / tickets.length) * 100) : 0}%` }}
            />
          </div>
        </div>
      </div>

      <h3 className="hq-sec">
        План до экзамена <span className="mono">пересобирается сам после каждого мока</span>
      </h3>
      <div className="hq-plan">
        {plan.map((d, i) =>
          'collapsed' in d ? (
            <div key={`c${i}`} className="hq-day">
              <div className="d">
                {d.from} … {d.to}
              </div>
              <div className="hq-day-tasks">
                {d.tasks.map((t) => (
                  <PlanChip key={t.id} task={t} onStartMock={onStartMock} onOpenFlashcards={onOpenFlashcards} onTicket={onTicket} setDrill={setDrillChapter} weakChapters={weakChapters} />
                ))}
              </div>
            </div>
          ) : (
            <div key={d.offset} className={`hq-day ${d.offset === 0 ? 'today' : ''}`}>
              <div className="d">{d.label}</div>
              <div className="hq-day-tasks">
                {d.tasks.map((t) => (
                  <PlanChip key={t.id} task={t} onStartMock={onStartMock} onOpenFlashcards={onOpenFlashcards} onTicket={onTicket} setDrill={setDrillChapter} weakChapters={weakChapters} />
                ))}
              </div>
            </div>
          ),
        )}
      </div>
      <p className="hq-plan-note">
        день пропущен — план тихо пересобирается, без красного и без чувства вины
      </p>

      <h3 className="hq-sec">
        Слабые главы{' '}
        <span className="mono">
          {byChapter ? 'из «Перевала» и моков · обновляется после каждого' : 'появятся после «Перевала» или Mock #1'}
        </span>
      </h3>
      {weakChapters.length > 0 && (
        <div className="hq-weak">
          {weakChapters.slice(0, 4).map(({ ch, pct }) => (
            <div key={ch} className="hq-wk">
              <span className="nm">{CTFL_CHAPTER_RU[ch] ?? ch}</span>
              <div className="hq-bar">
                <i className={pct >= 65 ? 'green' : 'mustard'} style={{ width: `${pct}%` }} />
              </div>
              {pct >= 65 ? (
                <span className="ok">норм · {pct}%</span>
              ) : (
                <button type="button" className="hq-drill-btn" onClick={() => setDrillChapter(ch)}>
                  дрилл · 10 вопросов
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <h3 className="hq-sec">
        Материалы спринта <span className="mono">открываются в любом порядке</span>
      </h3>
      <div className="hq-mats">
        {tickets.map((t) => {
          const st = progress.tickets[t.id]?.status;
          const done = st === 'done';
          const inProgress = st === 'in-progress';
          return (
            <button key={t.id} type="button" className="hq-mat" onClick={() => onTicket(t.id)}>
              <span className={`st ${done ? 'done' : inProgress ? 'now' : 'idle'}`}>
                {done ? 'пройден' : inProgress ? 'в работе' : 'впереди'}
              </span>
              <span className="nm">{t.title}</span>
              <span className="mt">{t.estimateMin} мин</span>
            </button>
          );
        })}
      </div>

      <h3 className="hq-sec">Быстрые действия</h3>
      <div className="hq-quick">
        <button type="button" className="hq-btn" onClick={() => onStartMock?.(mock1 ? 'mock2' : 'mock1')}>
          {mock1 ? 'Mock #2 →' : 'Mock #1 →'}
        </button>
        <button type="button" className="hq-btn teal" onClick={() => onOpenFlashcards?.()}>
          сессия глоссария
        </button>
        {/* Канон-учебник · статический HTML из 02_CONTENT, served-копия в
            app/public (синхронизируется при изменении источника). Открываем в
            новой вкладке — это standalone-справочник, не экран приложения. */}
        <button
          type="button"
          className="hq-btn ghost"
          onClick={() => window.open('/istqb_canon_textbook.html', '_blank', 'noopener,noreferrer')}
        >
          учебник-канон →
        </button>
        {weakChapters[0] && weakChapters[0].pct < 65 && (
          <button type="button" className="hq-btn ghost" onClick={() => setDrillChapter(weakChapters[0]!.ch)}>
            дрилл слабой главы
          </button>
        )}
      </div>

      {drillChapter && <DrillSession chapter={drillChapter} onClose={() => setDrillChapter(null)} />}
    </section>
  );
}

function PlanChip({
  task,
  onStartMock,
  onOpenFlashcards,
  onTicket,
  setDrill,
  weakChapters,
}: {
  task: PlanTask;
  onStartMock?: (slot: 'mock1' | 'mock2') => void;
  onOpenFlashcards?: () => void;
  onTicket: (id: string) => void;
  setDrill: (ch: string) => void;
  weakChapters: Array<{ ch: string; pct: number }>;
}) {
  const action = () => {
    if (task.kind === 'mock' && task.mockSlot) return onStartMock?.(task.mockSlot);
    if (task.kind === 'ticket' && task.ticketId) return onTicket(task.ticketId);
    if (task.kind === 'glossary') return onOpenFlashcards?.();
    if (task.kind === 'drill') {
      const weak = weakChapters.find((w) => w.pct < 65) ?? weakChapters[0];
      if (weak) setDrill(weak.ch);
    }
  };
  const actionable = task.kind !== 'rest' && task.kind !== 'review' && !task.done;
  return (
    <button
      type="button"
      className={`hq-task ${task.done ? 'done' : ''} ${task.kind === 'mock' && !task.done ? 'go' : ''}`}
      onClick={actionable ? action : undefined}
      disabled={!actionable}
    >
      {task.label}
    </button>
  );
}
